import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, InputAdornment,
  Paper, Stack, TextField, Typography,
} from "@mui/material";
import {
  getDocumentDetails, getDocumentPurchaseLines, getPurchaseLineFeatures,
  type PurchaseLineDTO, type PurchaseLineFeatures,
} from "../../api/documents";
import {
  addSettlementLine, changeSettlementAmount, changeSettlementFlags,
  type SettlementType,
} from "../../api/settlements";
import { formatCurrencyAmount } from "../../utils/formatters";
import { sumSettlementAmounts } from "../../utils/settlementAmounts";
import {
  parseSettlementMagnitude, planSettlementChange,
} from "../../utils/settlementSingleLine";

type Props = {
  companyId: string;
  documentId: string;
  currency: string;
  remainingAmount: number | null;
  canEditDocument: boolean;
  onUpdated: () => void;
  onBusyChange?: (busy: boolean) => void;
  onCancel: () => void;
  onSaved: () => void;
};

type Editor = { kind: SettlementType };
type PendingSkip = { lineId: string; checked: boolean };
type NewAmounts = Partial<Record<SettlementType, number>>;

const kinds: SettlementType[] = ["Charge", "Deduction"];
const label = (kind: SettlementType) => kind === "Charge" ? "Obciążenia" : "Odliczenia";

export default function DocumentSettlementSection({
  companyId, documentId, currency, remainingAmount, canEditDocument,
  onUpdated, onBusyChange, onCancel, onSaved,
}: Props) {
  const [features, setFeatures] = useState<PurchaseLineFeatures | null>(null);
  const [originalLines, setOriginalLines] = useState<PurchaseLineDTO[]>([]);
  const [lines, setLines] = useState<PurchaseLineDTO[]>([]);
  const [newAmounts, setNewAmounts] = useState<NewAmounts>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [amount, setAmount] = useState("");
  const [pendingSkip, setPendingSkip] = useState<PendingSkip | null>(null);
  // const [currentRemainingAmount, setCurrentRemainingAmount] = useState(remainingAmount);
  const [refreshFailed, setRefreshFailed] = useState(false);

  // useEffect(() => {
  //   setCurrentRemainingAmount(remainingAmount);
  // }, [remainingAmount]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const settings = await getPurchaseLineFeatures(companyId);
        if (!active) return;
        setFeatures(settings);
        if (!settings.useKSeFSettlementHandling) {
          setLines([]);
          setOriginalLines([]);
          return;
        }
        const data = await getDocumentPurchaseLines(companyId, documentId);
        if (active) {
          setOriginalLines(data);
          setLines(data.map((line) => ({ ...line })));
          setNewAmounts({});
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : "Nie udało się pobrać rozliczenia.");
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [companyId, documentId]);

  const changedLines = useMemo(() => {
    const originals = new Map(originalLines.map((line) => [line.systemId, line]));
    return lines.filter((line) => {
      const original = originals.get(line.systemId);
      return original && (
        original.directUnitCost !== line.directUnitCost ||
        original.skipInPosting !== line.skipInPosting ||
        original.skipInSettlement !== line.skipInSettlement
      );
    });
  }, [lines, originalLines]);

  const hasChanges = changedLines.length > 0 || kinds.some((kind) => newAmounts[kind] !== undefined);

  const edit = (kind: SettlementType) => {
    const row = lines.find((line) => line.settlementType === kind);
    const original = originalLines.find((line) => line.systemId === row?.systemId);
    const value = newAmounts[kind] ?? (row
      ? (original?.directUnitCost !== row.directUnitCost
        ? row.directUnitCost
        : row.amount ?? row.directUnitCost)
      : null);
    setAmount(value == null ? "" : String(Math.abs(value)));
    setEditor({ kind });
    setError(null);
  };

  const amountValidationError = useMemo(() => {
    if (!editor) return null;
    try {
      const signedAmount = parseSettlementMagnitude(editor.kind, amount);
      const kindLines = lines.filter((line) => line.settlementType === editor.kind);
      planSettlementChange(editor.kind, kindLines, signedAmount,
        Boolean(features?.allowManualSettlementLine && canEditDocument));
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : "Nieprawidłowa kwota rozliczenia.";
    }
  }, [editor, amount, lines, features, canEditDocument]);

  const saveDraftAmount = () => {
    if (!editor || amountValidationError) return;
    try {
      const signedAmount = parseSettlementMagnitude(editor.kind, amount);
      const kindLines = lines.filter((line) => line.settlementType === editor.kind);
      const plan = planSettlementChange(editor.kind, kindLines, signedAmount,
        Boolean(features?.allowManualSettlementLine && canEditDocument));
      if (plan.action === "create") {
        setNewAmounts((current) => ({ ...current, [editor.kind]: plan.amount }));
      } else {
        setLines((current) => current.map((line) =>
          line.systemId === plan.systemId
            ? { ...line, directUnitCost: plan.directUnitCost }
            : line
        ));
      }
      setEditor(null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Nie można zmienić kwoty rozliczenia.");
    }
  };

  const updateDraftFlag = (lineId: string, flag: "skipInPosting" | "skipInSettlement", checked: boolean) => {
    setLines((current) => current.map((line) =>
      line.systemId === lineId ? { ...line, [flag]: checked } : line
    ));
  };

  const saveDraftFlag = (
    row: PurchaseLineDTO,
    flag: "skipInPosting" | "skipInSettlement",
    checked: boolean,
  ) => {
    if (flag === "skipInPosting" && checked) {
      setPendingSkip({ lineId: row.systemId, checked });
      return;
    }
    updateDraftFlag(row.systemId, flag, checked);
  };

  const saveAll = async () => {
    if (busy || loading || refreshFailed || editor || pendingSkip) return;
    if (!hasChanges) {
      onCancel();
      return;
    }
    // Validate all planned BC operations BEFORE the first network request.
    const originals = new Map(originalLines.map((line) => [line.systemId, line]));
    try {
      for (const kind of kinds) {
        const existing = lines.filter((line) => line.settlementType === kind);
        const changedAmount = changedLines.find((line) =>
          line.settlementType === kind &&
          originals.get(line.systemId)?.directUnitCost !== line.directUnitCost
        );
        if (changedAmount) {
          const planned = planSettlementChange(kind, existing,
            Number(changedAmount.directUnitCost), false);
          if (planned.action !== "update" || planned.systemId !== changedAmount.systemId) {
            throw new Error("Nieprawidłowa operacja aktualizacji rozliczenia.");
          }
        }
        const newAmount = newAmounts[kind];
        if (newAmount !== undefined) {
          // POST is allowed only if the row is absent from the document.
          const planned = planSettlementChange(kind, existing, newAmount,
            Boolean(features?.allowManualSettlementLine && canEditDocument));
          if (planned.action !== "create") {
            throw new Error("Nie można dodać drugiego wiersza tego samego typu.");
          }
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Nieprawidłowe wartości rozliczenia.");
      return;
    }

    setBusy(true);
    onBusyChange?.(true);
    setError(null);
    let savedOperations = 0;
    try {
      for (const line of changedLines) {
        const original = originals.get(line.systemId);
        if (!original) continue;
        if (original.directUnitCost !== line.directUnitCost) {
          await changeSettlementAmount(companyId, documentId, line.systemId, line.directUnitCost);
          savedOperations++;
        }
        const flags: { skipInPosting?: boolean; skipInSettlement?: boolean } = {};
        if (original.skipInPosting !== line.skipInPosting) flags.skipInPosting = line.skipInPosting;
        if (original.skipInSettlement !== line.skipInSettlement) flags.skipInSettlement = line.skipInSettlement;
        if (Object.keys(flags).length) {
          await changeSettlementFlags(companyId, documentId, line.systemId, flags);
          savedOperations++;
        }
      }
      for (const kind of kinds) {
        const newAmount = newAmounts[kind];
        if (newAmount !== undefined) {
          await addSettlementLine(companyId, documentId, kind, newAmount);
          savedOperations++;
        }
      }
      // All writes succeeded: refresh the parent once, after closing the modal.
      onSaved();
    } catch (e) {
      const reason = e instanceof Error ? e.message : "Nie udało się zapisać zmian.";
      // Even a failed request can have been applied by BC before the network
      // failure. Always re-read the source of truth before another save.
      onUpdated();
      try {
        const [freshLines/*, freshDocument*/] = await Promise.all([
          getDocumentPurchaseLines(companyId, documentId),
          getDocumentDetails(companyId, documentId),
        ]);
        setOriginalLines(freshLines);
        setLines(freshLines.map((line) => ({ ...line })));
        setNewAmounts({});
        // setCurrentRemainingAmount(freshDocument.amounts.remainingAmount);
        setError(
          `${savedOperations > 0 ? "Część zmian mogła zostać zapisana. " : "Nie udało się zakończyć zapisu. "}${reason} ` +
          "Dane pobrano ponownie z BC. Sprawdź, które zmiany zostały zapisane, i wprowadź brakujące jeszcze raz."
        );
      } catch {
        setRefreshFailed(true);
        setError(
          `Nie udało się potwierdzić wszystkich zmian. ${reason} ` +
          "Nie udało się odświeżyć danych z BC. Kliknij Anuluj, aby wrócić do dokumentu."
        );
      }
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <CircularProgress size={18} /> Pobieranie rozliczenia KSeF...
        <DialogActions>
          <Button onClick={onCancel}>Anuluj</Button>
        </DialogActions>
      </Paper>
    );
  }
  if (!features?.useKSeFSettlementHandling) {
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && <Typography>Obsługa obciążeń i odliczeń jest wyłączona.</Typography>}
        <DialogActions>
          <Button onClick={onCancel}>Zamknij</Button>
        </DialogActions>
      </Paper>
    );
  }

  const settlementLines = lines.filter(
    (line) => line.settlementType === "Charge" || line.settlementType === "Deduction"
  );
  const allowsManual = features.allowManualSettlementLine && canEditDocument;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Obciążenia i odliczenia KSeF</Typography>
      {/* {currentRemainingAmount != null && !refreshFailed && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Do zapłaty{hasChanges ? " (przed zapisaniem zmian)" : ""}: {formatCurrencyAmount(currentRemainingAmount)} {currency}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Wiersze rozliczenia mają stawkę NP. Zmiany zostaną przesłane do Business Central
        dopiero po kliknięciu „Zapisz”. „Anuluj” odrzuca niezapisane zmiany.
      </Typography> */}
      {hasChanges && <Alert severity="warning" sx={{ mb: 2 }}>Masz niezapisane zmiany.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={2}>
        {kinds.map((kind) => {
          const kindLines = settlementLines.filter((line) => line.settlementType === kind);
          const row = kindLines.length === 1 ? kindLines[0] : undefined;
          const original = originalLines.find((item) => item.systemId === row?.systemId);
          const pendingAmount = newAmounts[kind];
          // On the new BC contract a single settlement line represents the amount.
          // For an unsaved edit preview directUnitCost, otherwise use BC's amount.
          const total = pendingAmount !== undefined ? pendingAmount
            : row ? (original?.directUnitCost !== row.directUnitCost
              ? Number(row.directUnitCost)
              : Number(row.amount ?? row.directUnitCost ?? 0))
            : sumSettlementAmounts(kindLines, originalLines);
          const hasDraftAmount = pendingAmount !== undefined || Boolean(row &&
            original?.directUnitCost !== row.directUnitCost);
          const canEditAmount = canEditDocument && !refreshFailed &&
            (row
              ? row.settlementAmountEditable && !row.lineMatched &&
                Number(row.quantity ?? 1) === 1
              : kindLines.length === 0 && allowsManual);
          return (
            <Box key={kind} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography variant="subtitle2">{label(kind)} — NP</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrencyAmount(total)} {currency}
                    {hasDraftAmount ? " (niezapisane zmiany)" : ""}
                  </Typography>
                  {kindLines.length === 0 && pendingAmount === undefined && (
                    <Typography variant="caption" color="text.secondary">
                      Brak pozycji tego rodzaju.
                    </Typography>
                  )}
                </Box>
                {canEditAmount && (
                  <Button size="small" variant="outlined" disabled={busy || refreshFailed}
                    onClick={() => edit(kind)}>
                    {kindLines.length === 0 && pendingAmount === undefined ? "Dodaj" : "Zmień kwotę"}
                  </Button>
                )}
              </Stack>
              {kindLines.length > 1 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Dokument ma kilka wierszy {kind === "Charge" ? "obciążeń" : "odliczeń"} w BC.
                  Można sprawdzić ich łączną kwotę, ale edycja wymaga uporządkowania danych w BC.
                </Alert>
              )}
              {row && (
                <>
                  {(row.skipInPostingAllowed || row.skipInPosting || row.lineEditable || row.skipInSettlement) &&
                    <Divider sx={{ my: 1 }} />}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }}>
                    {(row.skipInPostingAllowed || row.skipInPosting) && (
                      <FormControlLabel
                        label="Pomiń w księgowaniu"
                        control={<Checkbox size="small" checked={row.skipInPosting}
                          disabled={busy || refreshFailed || (!row.skipInPostingAllowed && !row.skipInPosting)}
                          onChange={(_, checked) => saveDraftFlag(row, "skipInPosting", checked)} />}
                      />
                    )}
                    {(row.lineEditable || row.skipInSettlement) && (
                      <FormControlLabel
                        label="Pomiń w rozrachunkach"
                        control={<Checkbox size="small" checked={row.skipInSettlement}
                          disabled={busy || refreshFailed || (!row.lineEditable && !row.skipInSettlement)}
                          onChange={(_, checked) => saveDraftFlag(row, "skipInSettlement", checked)} />}
                      />
                    )}
                  </Stack>
                  {row.skipInPosting && row.skipInSettlement && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Obie opcje są zaznaczone. W BC pierwszeństwo ma pominięcie w rozrachunkach:
                      wiersz zostanie zaksięgowany, ale nie zwiększy kwoty do zapłaty.
                    </Alert>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Stack>
      <DialogActions sx={{ px: 0, pb: 0, mt: 2 }}>
        <Button disabled={busy} onClick={onCancel}>Anuluj</Button>
        <Button variant="contained" disabled={busy || refreshFailed}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
          onClick={() => void saveAll()}>Zapisz</Button>
      </DialogActions>
      <Dialog open={Boolean(editor)} onClose={busy ? undefined : () => setEditor(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editor && (
          `${lines.some((line) => line.settlementType === editor.kind) ? "Zmień kwotę" : "Dodaj"}: ` +
          (editor.kind === "Charge" ? "obciążenia" : "odliczenia")
        )}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
          <TextField
            fullWidth autoFocus margin="normal"
            label={editor?.kind === "Charge" ? "Kwota obciążenia" : "Kwota odliczenia"}
            type="number" size="small" value={amount} disabled={busy}
            inputProps={{ min: 0, step: "0.01" }}
            InputProps={editor?.kind === "Deduction" ? {
              startAdornment: <InputAdornment position="start">−</InputAdornment>,
            } : undefined}
            onChange={(e) => { setAmount(e.target.value); setError(null); }}
            error={Boolean(amount.trim() && amountValidationError)}
            helperText={amount.trim() && amountValidationError
              ? amountValidationError
              : editor?.kind === "Deduction"
                ? "Wpisz dodatnią wartość bez minusa. Odliczenie otrzyma znak minus."
                : "Wpisz dodatnią kwotę obciążenia."}
          />
          {editor?.kind === "Deduction" && amount.trim() && Number.isFinite(Number(amount.replace(",", "."))) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Kwota odliczenia w BC: {formatCurrencyAmount(-Math.abs(Number(amount.replace(",", "."))))} {currency}
            </Typography>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            {editor?.kind === "Deduction"
              ? "Podaj kwotę odliczenia bez minusa."
              : "Podaj kwotę obciążenia."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setEditor(null)}>Anuluj</Button>
          <Button variant="contained" disabled={busy || Boolean(amountValidationError)} onClick={saveDraftAmount}>
            Zatwierdź kwotę
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(pendingSkip)} onClose={busy ? undefined : () => setPendingSkip(null)}>
        <DialogTitle>Pomiń w księgowaniu?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Przy zatwierdzeniu wiersz zostanie nieodwracalnie usunięty wraz z jego wymiarami
            z faktury, o ile nie wybrano również „Pomiń w rozrachunkach”.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setPendingSkip(null)}>Anuluj</Button>
          <Button variant="contained" color="warning" disabled={busy}
            onClick={() => {
              if (!pendingSkip) return;
              updateDraftFlag(pendingSkip.lineId, "skipInPosting", pendingSkip.checked);
              setPendingSkip(null);
            }}>Potwierdź</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
